const quote = (value) => `'${String(value ?? "").replace(/'/g, `'\\''`)}'`;

const CONTENT_TYPES = {
  json: "application/json",
  xml: "application/xml",
  text: "text/plain",
  "form-urlencoded": "application/x-www-form-urlencoded",
};

const parseJson = (value, fallback) => {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string" || !value.trim()) return fallback;
  try {
    return JSON.parse(value) ?? fallback;
  } catch {
    return fallback;
  }
};

export const applyPathParams = (url, pathParams) => {
  const entries = Object.entries(pathParams || {});
  if (!entries.length) return url || "";

  return entries.reduce((acc, [key, value]) => {
    const encoded = encodeURIComponent(value ?? "");
    return acc
      .replace(new RegExp(`\\{${key}\\}`, "g"), encoded)
      .replace(new RegExp(`:${key}(?=\\/|$)`, "g"), encoded);
  }, url || "");
};

const withQueryString = (url, queryParams) => {
  const pairs = Object.entries(queryParams || {}).filter(([key]) => key);
  if (!pairs.length) return url;

  const query = pairs
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value ?? "")}`)
    .join("&");
  return url.includes("?") ? `${url}&${query}` : `${url}?${query}`;
};

/** The `-F` flags for a multipart body, whose fields are stored as `{ fields: [...] }`. */
const formDataFlags = (body) =>
  (parseJson(body, {}).fields || [])
    .filter((field) => field?.key)
    .map((field) =>
      field.type === "file"
        ? `-F ${quote(`${field.key}=@${field.fileName || field.value || "file"}`)}`
        : `-F ${quote(`${field.key}=${field.value ?? ""}`)}`,
    );

export const buildCurlCommand = ({
  method = "GET",
  url = "",
  headers = {},
  queryParams = {},
  pathParams = {},
  body = "",
  bodyType = "json",
} = {}) => {
  const target = withQueryString(applyPathParams(url, pathParams), queryParams);
  const flags = [`-X ${String(method || "GET").toUpperCase()}`];

  const headerEntries = Object.entries(headers || {}).filter(([key]) => key);
  headerEntries.forEach(([key, value]) => flags.push(`-H ${quote(`${key}: ${value ?? ""}`)}`));

  const type = bodyType || "json";
  const hasContentType = headerEntries.some(([key]) => key.toLowerCase() === "content-type");

  if (type === "form-data") {
    // curl builds its own multipart Content-Type, boundary included, so none is added here.
    flags.push(...formDataFlags(body));
  } else if (type === "binary") {
    // The editor keeps the file as base64 metadata; a path is what curl can actually send.
    const { fileName } = parseJson(body, {});
    if (fileName) flags.push(`--data-binary ${quote(`@${fileName}`)}`);
  } else if (type !== "none" && body) {
    if (!hasContentType && CONTENT_TYPES[type]) {
      flags.push(`-H ${quote(`Content-Type: ${CONTENT_TYPES[type]}`)}`);
    }
    flags.push(`--data-raw ${quote(body)}`);
  }

  return [`curl ${quote(target)}`, ...flags].join(" \\\n  ");
};
